'use client';

import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { usePathname } from '@/i18n/routing';
import useAccount from '@/lib/hooks/useAccount';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import useCart from '@/lib/hooks/useCart';
import {
  pushPageLoad,
  pushCartContentsPush,
  pushUserIdentifyForAccount,
  type UserStatus,
  type CartItem,
  type CountryCode,
} from '@/lib/analytics/dataLayer';
import { getPageType } from '@/lib/analytics/pageType';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import type { LineItem } from '@shared/types/cart/LineItem';

function lineItemsToCartItems(lineItems: LineItem[] = []): CartItem[] {
  return lineItems.map((item) => ({
    item_id: item.lineItemId ?? item.productId ?? '',
    item_name: item.name ?? '',
    price: (item.price?.centAmount ?? 0) / Math.pow(10, item.price?.fractionDigits ?? 2),
    quantity: item.count ?? 1,
  }));
}

export default function DataLayerProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { locale } = useParams<{ locale: string }>();
  const { account, loggedIn } = useAccount();

  // EWM-583 mapped locale → market. Mirrors the registration-form derivation.
  const countryCode: CountryCode = (locale ?? '').toUpperCase().includes('NZ') ? 'nz' : 'au';

  // EWM-482 — BU/store come from the StoreAndBusinessUnits context (this
  // provider now lives inside <AppProviders> in the [locale] layout, above this
  // component). No more localStorage bridge.
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits();
  const buKey = selectedBusinessUnit?.key;
  const storeKey = selectedStore?.key;

  const { permissions } = useAccountRoles(buKey);
  const { cart, isLoading, totalItems } = useCart(buKey, storeKey);

  // Tracks the last page context for which page_load fired — prevents
  // double-firing on re-renders that don't represent a new page view.
  const pageFiredForRef = useRef<string | null>(null);

  // Effect 1: page_load — fires once per unique navigation (pathname +
  // search + BU context). Does NOT re-fire when only cart content changes.
  useEffect(() => {
    if (isLoading) return;

    // Include buKey in the discriminator: when BU key loads after
    // anonymous render, we want to re-fire with the correct B2B cart.
    const pageKey = `${pathname}?${searchParams.toString()}|${buKey ?? ''}`;
    if (pageFiredForRef.current === pageKey) return;
    pageFiredForRef.current = pageKey;

    const userStatus: UserStatus = !loggedIn ? 'guest' : permissions.UpdateMyCarts ? 'authed' : 'view_only';

    // customer_group: use CT account group name if available, fall back to
    // 'dealer' for any authenticated user on this B2B site.
    // TODO: confirm exact CT customer-group tier names with the team once
    // EWM-368 open question is resolved.
    const customerGroup = !loggedIn
      ? 'guest'
      : ((cart?.accountGroup as { name?: string } | undefined)?.name ?? 'dealer');

    const deliveryPostcode = account?.addresses?.find((a) => a.isDefaultShippingAddress)?.postalCode;

    pushPageLoad({
      page_type: getPageType(pathname, searchParams.get('query')),
      user_status: userStatus,
      customer_group: customerGroup,
      ...(loggedIn && {
        business_unit: cart?.businessUnitKey,
        account_status: account?.isActive ? 'active' : 'inactive',
      }),
      ...(deliveryPostcode && { delivery_postcode: deliveryPostcode }),
    });
  }, [pathname, searchParams, isLoading, cart, account, loggedIn, permissions, buKey]);

  // Tracks the last accountId identified to Braze — prevents re-firing on
  // every render / navigation once a session is established.
  const identifiedAccountRef = useRef<string | null>(null);

  // Effect 3: userIdentify — analytics identity (EWM-368 / EWM-583). Fires once
  // per authenticated account, covering already-authenticated sessions restored
  // on a fresh page load (not just sign-in / registration).
  //
  // NOTE (EWM-491): the comms-preferences sync no longer depends on this. The
  // BFF now keys Braze reads/writes on the customer's EMAIL, not the CT id, so
  // it finds the profile server-side without an SDK identify. GTM still maps
  // customer_id → braze.changeUser(customer_id), but Braze's external_id is a
  // legacy SF_con_id that CT doesn't hold — so that changeUser can't match the
  // real profile and is pending the SF_con_id → CT external_id migration
  // (Luke/Leo). Tracked separately; left as-is here to avoid touching analytics.
  useEffect(() => {
    if (!loggedIn || !account?.accountId) {
      // Reset on logout so the next (possibly different) customer re-identifies.
      identifiedAccountRef.current = null;
      return;
    }
    if (identifiedAccountRef.current === account.accountId) return;
    identifiedAccountRef.current = account.accountId;
    // Fire-and-forget (the helper hashes async); never block render.
    void pushUserIdentifyForAccount(account.email ?? '', account, countryCode);
  }, [loggedIn, account, countryCode]);

  // Tracks the last cart state for which cart_contents_push fired — keyed
  // by item count + cart total + cartId so mutations on the same page still
  // re-fire without needing navigation.
  const cartFiredForRef = useRef<string | null>(null);

  // Effect 2: cart_contents_push — fires on navigation AND after any cart
  // mutation (add/remove item). Satisfies spec: "fires after page_load,
  // after add_to_cart, after remove_from_cart".
  useEffect(() => {
    if (isLoading) return;

    const cartKey = `${totalItems}|${cart?.sum?.centAmount ?? 0}|${cart?.cartId ?? ''}|${buKey ?? ''}`;
    if (cartFiredForRef.current === cartKey) return;
    cartFiredForRef.current = cartKey;

    const cartTotal = (cart?.sum?.centAmount ?? 0) / Math.pow(10, cart?.sum?.fractionDigits ?? 2);

    pushCartContentsPush({
      cart_total: cartTotal,
      cart_num_items: totalItems,
      cart_items: lineItemsToCartItems(cart?.lineItems),
      latest_cart_item: [],
    });
  }, [isLoading, cart, buKey, totalItems]);

  return null;
}
